import { formatEther } from "@ethersproject/units";
import { intervalToDuration } from "date-fns";
import { format } from "date-fns-tz";

// Constants
import { AVAIALABLE_TO_BE_CLAIMED } from "constants/constants";
import { CHECKPOINT_DURATION } from "constants/contracts";

// Types
import { Withdrawal } from "types/common";

export const openLinkInNewTab = (link: string | undefined) =>
  window.open(link, "_blank");

export const copyToClipboard = (value: any) =>
  navigator.clipboard.writeText(value);

export const setLocalStorageValue = (key: string, value: any) =>
  window?.localStorage.setItem(key, JSON.stringify(value));

export const getLocalStorageValue = (key: string) =>
  window?.localStorage.getItem(key);

export const isFloat = (n: number) => {
  return Number(n) === n && n % 1 !== 0;
};

export const tzAbbr = () =>
  new Date()
    .toLocaleDateString(undefined, {
      day: "2-digit",
      timeZoneName: "long",
    })
    .slice(4)
    .split(" ")
    .map((item) => item[0])
    .join("");

export const numberToCurrency = (value: string) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(value)) >= 1.0e9
    ? +(Math.abs(Number(value)) / 1.0e9).toFixed(2) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(value)) >= 1.0e6
    ? +(Math.abs(Number(value)) / 1.0e6).toFixed(2) + "M"
    : // Three Zeroes for Thousands
    Math.abs(Number(value)) >= 1.0e3
    ? +(Math.abs(Number(value)) / 1.0e3).toFixed(2) + "K"
    : Math.abs(Number(value)).toString();
};

export const currencyToNumber = (value: string) => {
  const c = value.slice(-1).toUpperCase();
  switch (c) {
    case "K":
      return +value.slice(0, -1) * 10e3;
    case "M":
      return +value.slice(0, -1) * 10e6;
    case "B":
      return +value.slice(0, -1) * 10e9;

    default:
      +value;
  }
};

export const toFixedWithoutRounding = (value: string, decimals: number) =>
  (value.match(new RegExp(`^-?\\d+(?:.\\d{0,${decimals}})?`)) as string[])[0];

export const formatEtherNumber = (value: string, decimals = 4) => {
  const number = toFixedWithoutRounding(
    formatEther(value.toString()),
    decimals
  );

  return number;
};

export const getWithdrawalsWithClaimStatus = (
  withdrawals: Withdrawal[],
  currentEpoch: number
) => {
  withdrawals.sort((one, two) => one.validatorNonce - two.validatorNonce);
  return withdrawals.map((withdrawal) => {
    const maturingEpoch = withdrawal.requestEpoch;
    const remainingEpoch = maturingEpoch - currentEpoch;

    const isClaimable = remainingEpoch <= 0;

    const remainingHours = Math.ceil(
      (CHECKPOINT_DURATION * remainingEpoch) / 60
    );
    let remainingTime;

    if (remainingHours <= 24) {
      remainingTime =
        +remainingHours <= 1 ? "1 hour" : remainingHours + " hours";
    } else {
      const days = Math.ceil(remainingHours / 24);
      remainingTime = days <= 1 ? "1 day" : days + " days";
    }

    const message = isClaimable
      ? AVAIALABLE_TO_BE_CLAIMED
      : `${remainingEpoch} checkpoints left. (~${remainingTime})`;

    return {
      ...withdrawal,
      isClaimable,
      remainingEpoch: remainingEpoch < 0 ? 0 : remainingEpoch,
      message,
    };
  });
};

export const getClaimRequestsForPolygon = (withdrawals: Withdrawal[]) => {
  withdrawals.sort((one, two) => one.withdrawalTime - two.withdrawalTime);

  return withdrawals.map((withdrawal) => {
    const remainingEpoch =
      withdrawal.withdrawalTime - +(new Date().getTime() / 1000).toFixed(0);

    const isClaimable = remainingEpoch <= 0;

    const remainingTime = new Date(withdrawal.withdrawalTime * 1000);

    const message = isClaimable
      ? AVAIALABLE_TO_BE_CLAIMED
      : `${format(remainingTime, "MMM dd, yyyy hh:mm a")} (${tzAbbr()})`;

    return {
      ...withdrawal,
      isClaimable,
      remainingEpoch: remainingEpoch < 0 ? 0 : remainingEpoch,
      message,
    };
  });
};

export const getDuration = (startTime: Date, endTime: Date): string => {
  const duration = intervalToDuration({
    start: new Date(startTime),
    end: endTime,
  });
  const durationAsHours: number = Math.floor(duration.hours as number);
  const restInMinutes: number = duration.minutes as number;
  const hours: string =
    durationAsHours > 1
      ? `${durationAsHours} hrs`
      : durationAsHours > 0
      ? `${durationAsHours} hr`
      : "";

  if (hours) {
    return hours;
  }

  const minutes: string =
    restInMinutes === 1 ? `${restInMinutes} min` : `${restInMinutes} mins`;

  if (restInMinutes) {
    return minutes;
  }

  const seconds = `${duration.seconds} secs`;

  return seconds;
};

export const getNumbersFromValue = (value: string) => {
  if (!value) {
    return 0;
  }
  const regex = /[+-]?\d+(\.\d+)?/g;

  const res = value.match(regex)?.map((v) => parseFloat(v))[0];

  return res || 0;
};

export const toStringPrecision = (num: number) => {
  let res = "";

  if (Math.abs(num) < 1.0) {
    const e = parseInt(num.toString().split("e-")[1]);
    if (e) {
      num *= Math.pow(10, e - 1);
      res = "0." + new Array(e).join("0") + num.toString().substring(2);
    }
  } else {
    let e = parseInt(num.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      num /= Math.pow(10, e);
      res = num + new Array(e + 1).join("0");
    }
  }

  return res || num.toString();
};

export const isNumber = (str: string) => {
  if (str.trim() === "") {
    return false;
  }

  return !isNaN(+str);
};

export const shortenAddress = (address: string) =>
  `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;

export const validateEmail = (email: string) => {
  const emailReg = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return emailReg.test(email);
};

export const getQueryString = (obj: any) =>
  Object.keys(obj).reduce((acc, key) => {
    let str = acc;
    const value = obj[key];

    if (Array.isArray(value)) {
      if (value.length) {
        str += `${value
          .map((v) => `${key}=${encodeURIComponent(v)}&`)
          .join("")}`;
      }
    } else if (value) {
      str += `${key}=${encodeURIComponent(value)}&`;
    }

    return str;
  }, "");
